import { z } from 'zod';

import { timestamp } from '~/services/types.ts';

export const cargoStatus = z.enum(['Pending', 'InProgress', 'Completed']);
export const cargoOverviewSchema = z
  .object({
    batchCount: z.int(),
    createdAt: timestamp,
    id: z.string(),
    isAdditionalCargo: z.boolean(),
    isOwnerOverlift: z.boolean(),
    lastUpdatedAt: timestamp,
    receivedQuantityKilograms: z.int(),
    plannedQuantityKilograms: z.int(),
    totalStoredKilograms: z.int(),
    status: cargoStatus,
    totalEmissionsKilograms: z.int(),
    voyageReferenceNo: z.string().nullish().transform(v => v ?? ''),
  });

export const batchStatus = z.enum([
  'Pending',
  'InTransit',
  'InStorageTanks',
  'InPipeline',
  'Stored',
]);
export const batchType = z.enum(['Payload', 'Heel', 'Reclaim', 'Comissioning']); // sic on Comissioning — backend C# enum BatchType
export const emissionType = z.enum(['Direct', 'Operational', 'Amortized']);
export const emissionCategory = z.enum([
  'UncategorizedLosses',
  'Venting',
  'Fugitive',
  'LeakageAndLosses',
  'FuelConsumption',
  'GridPower',
  'MeasurementDifference',
  'Commissioning',
  'LeakageDetectionSystem',
  'ShorePower',
  'Construction',
  'UnallocatableEmissions',
  'Decommissioning',
]);

export const detailedEmissionSchema = z.object({
  id: z.string(),
  type: emissionType,
  category: emissionCategory,
  quantityKilograms: z.int(),
});

const transportStop = z.object({
  id: z.string(),
  terminalId: z.string(),
  terminalName: z.string(),
  estimatedTimeArrival: timestamp.nullable(),
  actualTimeArrival: timestamp.nullable(),
  estimatedTimeDeparture: timestamp.nullable(),
  actualTimeDeparture: timestamp.nullable(),
});

const childBatchSchema = z.object({
  id: z.string(),
  quantityKilograms: z.int(),
  createdAt: timestamp,
});

const transportSchehma = z.object({
  loadedKilograms: z.int(),
  emissions: detailedEmissionSchema.array(),
  loadingStop: transportStop,
  unloadingStop: transportStop,
  heel: childBatchSchema.nullable(),
  totalEmissionsKilograms: z.int(),
});

const storageSchema = z.object({
  terminalName: z.string(),
  queuePosition: z.int(),
  receivedKilograms: z.int(),
  storedKilograms: z.int(),
  emissions: detailedEmissionSchema.array(),
  receivedAt: timestamp.nullable(),
  injectedAt: timestamp.nullable(),
  storedAt: timestamp.nullable(),
  reclaims: childBatchSchema.array(),
  totalEmissionsKilograms: z.int(),
});

const batchSchema = z.object({
  id: z.string(),
  type: batchType,
  storage: storageSchema,
  transport: transportSchehma.nullable(),
  invoiceId: z.string().optional().nullable(),
  certificateId: z.string().optional().nullable(),
  createdAt: timestamp,
  lastUpdatedAt: timestamp,
  status: batchStatus,
  totalEmissionsKilograms: z.int(),
  parentId: z.string().nullable(),
  cargoId: z.string(),
});

export const cargoSchema = z.object({
  batches: batchSchema.array(),
  bindingQuantityKilograms: z.int(),
  committedQuantityKilograms: z.int(),
  createdAt: timestamp,
  id: z.string(),
  isAdditionalCargo: z.boolean(),
  isOwnerBindingOverlift: z.boolean(),
  isOwnerCommittedOverlift: z.boolean(),
  lastUpdatedAt: timestamp,
  projectId: z.string(),
  receivedQuantityKilograms: z.int(),
  status: cargoStatus,
  totalEmissionsKilograms: z.int(),
  totalStoredKilograms: z.int(),
  voyageReferenceNo: z.string().nullish().transform(v => v ?? ''),
});

export type DetailedEmission = z.infer<typeof detailedEmissionSchema>;
export type DetailedBatch = z.infer<typeof batchSchema>;
export type DetailedCargo = z.infer<typeof cargoSchema>;
export type CargoOverview = z.infer<typeof cargoOverviewSchema>;
export type CargoStatus = z.infer<typeof cargoStatus>;
export type BatchStatus = z.infer<typeof batchStatus>;
export type BatchType = z.infer<typeof batchType>;
export type EmissionCategory = z.infer<typeof emissionCategory>;
